<template>
  <v-dialog v-model="newUserDialog" max-width="800px" persistent scrollable>
    <v-card class="new-user-dialog">
      <!-- Header Section -->
      <v-card-title class="dialog-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <v-icon color="white" size="24">person_add</v-icon>
          </div>
          <div class="header-text">
            <h2 class="dialog-title">Add New Staff Member</h2>
            <p class="dialog-subtitle">
              Create a new staff account with appropriate permissions
            </p>
          </div>
        </div>
        <v-btn icon @click="$emit('setDialog', false)" class="close-btn">
          <v-icon color="white">close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Form Content -->
      <v-card-text class="dialog-content">
        <v-form ref="form" v-model="valid" lazy-validation>
          <!-- Contact Information Section -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="primary" class="mr-2">contact_phone</v-icon>
              <h3 class="section-title">Contact Information</h3>
            </div>

            <v-layout row>
              <v-flex xs3 class="mr-2">
                <v-select
                  :items="country_tel"
                  v-model="user_country_tel"
                  label="Country Code"
                  outline
                  prepend-inner-icon="flag"
                  class="country-select"
                ></v-select>
              </v-flex>
              <v-flex xs9 class="ml-2">
                <v-text-field
                  v-model="tel"
                  mask="## - ### - ####"
                  label="Phone Number"
                  outline
                  prepend-inner-icon="phone"
                  :rules="[telValid.leng, telValid.required, telValid.already]"
                  class="phone-field"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-text-field
              v-model="email"
              label="Email Address"
              outline
              prepend-inner-icon="email"
              type="email"
              :rules="[
                emailValid.email,
                emailValid.required,
                emailValid.already,
                nameValid.max,
              ]"
              class="email-field"
            ></v-text-field>
          </div>

          <!-- Personal Information Section -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="primary" class="mr-2">person</v-icon>
              <h3 class="section-title">Personal Information</h3>
            </div>

            <v-layout row>
              <v-flex xs6 class="mr-2">
                <v-text-field
                  v-model="fname"
                  label="First Name"
                  outline
                  prepend-inner-icon="person"
                  :rules="[nameValid.max, nameValid.required]"
                  class="name-field"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 class="ml-2">
                <v-text-field
                  v-model="lname"
                  label="Last Name"
                  outline
                  prepend-inner-icon="person_outline"
                  :rules="[nameValid.max, nameValid.required]"
                  class="name-field"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </div>

          <!-- Role & Permissions Section -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="primary" class="mr-2">security</v-icon>
              <h3 class="section-title">Role & Permissions</h3>
            </div>

            <v-select
              :items="RoleItems"
              item-text="name"
              item-value="value"
              v-model="role"
              label="Staff Role"
              outline
              prepend-inner-icon="admin_panel_settings"
              :rules="[roleValid.required]"
              class="role-select"
            ></v-select>

            <div class="province-section">
              <v-radio-group
                v-model="user_province_type"
                row
                class="province-radio"
              >
                <v-radio
                  label="All Provinces"
                  :value="1"
                  color="primary"
                ></v-radio>
                <v-radio
                  label="Specific Provinces"
                  :value="2"
                  color="primary"
                ></v-radio>
              </v-radio-group>

              <v-autocomplete
                v-if="user_province_type == 2"
                v-model="user_province"
                :items="province_list"
                outline
                :loading="loadingProvince"
                item-text="province_name_en"
                item-value="province_name_en"
                chips
                small-chips
                deletable-chips
                label="Select Provinces"
                multiple
                prepend-inner-icon="location_on"
                class="province-select"
              ></v-autocomplete>
            </div>
          </div>

          <!-- Security Section -->
          <div class="form-section">
            <div class="section-header">
              <v-icon color="primary" class="mr-2">lock</v-icon>
              <h3 class="section-title">Security Settings</h3>
            </div>

            <v-layout row>
              <v-flex xs6 class="mr-2">
                <v-text-field
                  v-model="password1"
                  :append-icon="show1 ? 'visibility_off' : 'visibility'"
                  :type="show1 ? 'text' : 'password'"
                  label="Password"
                  outline
                  prepend-inner-icon="lock"
                  autocomplete="new-password"
                  hint="At least 6 characters"
                  @click:append="show1 = !show1"
                  :rules="[passwordValid.min, passwordValid.max]"
                  class="password-field"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 class="ml-2">
                <v-text-field
                  v-model="password2"
                  :append-icon="show2 ? 'visibility_off' : 'visibility'"
                  :type="show2 ? 'text' : 'password'"
                  label="Confirm Password"
                  outline
                  prepend-inner-icon="lock_outline"
                  autocomplete="new-password"
                  hint="Re-enter password"
                  @click:append="show2 = !show2"
                  :rules="[passwordValid.min, passwordValid.max]"
                  class="password-field"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-alert
              v-if="passwordMatch == false"
              type="error"
              outline
              class="password-alert"
            >
              <v-icon slot="prepend">warning</v-icon>
              Passwords do not match. Please check and try again.
            </v-alert>
          </div>
        </v-form>
      </v-card-text>

      <!-- Footer Actions -->
      <v-card-actions class="dialog-footer">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="isEditStaff || !passwordMatch"
          @click="addUser() && clear()"
          class="save-btn"
        >
          <v-icon left>save</v-icon>
          Create Staff Member
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from "vuex";
export default {
  props: ["newUserDialog"],
  data() {
    return {
      user_province_type: 1,
      loadingProvince: false,
      user_province: [],
      user_country_tel: "+66",
      country_tel: ["+66"],
      passwordMatch: true,
      RoleItems: [
        {
          name: "SuperAdmin",
          value: 1,
        },
        {
          name: "Admin",
          value: 2,
        },
        {
          name: "Viewer(Manager)",
          value: 3,
        },
        {
          name: "Callcenter",
          value: 4,
        },
        {
          name: "Customer",
          value: 6,
        },
        {
          name: "RiderManager",
          value: 8,
        },
        {
          name: "Accounting",
          value: 9,
        },
        {
          name: "Marketing",
          value: 10,
        },
        {
          name: "AdminLaborStore",
          value: 11,
        },
        {
          name: "CallCenterAdmin",
          value: 13,
        },
      ],
      password1: "",
      password2: "",
      show1: false,
      show2: false,
      tel: "",
      verify: "",
      email: "",
      fname: "",
      lname: "",
      role: 1,
      valid: true,
      telValid: {
        required: (value) => !!value || "Required.",
        leng: (v) => v.length == 9 || "9 characters",
        already: (a) => !this.telAlready(a) || "Already in use",
      },
      nameValid: {
        required: (value) => !!value || "Required.",
        max: (v) => v.length <= 55 || "Min 55 characters",
      },
      emailValid: {
        already: (a) => !this.emailAlready(a) || "Already in use",
        required: (value) => !!value || "Required.",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        },
      },
      roleValid: {
        required: (value) => !!value || "Required.",
      },
      passwordValid: {
        min: (v) => v.length >= 6 || "At least 6 characters",
        max: (v) => v.length <= 24 || "No more than 24 characters",
      },
    };
  },
  watch: {
    async newUserDialog() {
      if (this.newUserDialog == true) {
        this.loadingProvince = true;
        await this.$store.dispatch("fetchProvinceBack/getData");
        this.loadingProvince = false;
      } else {
        this.clear();
      }
    },
    password1() {
      if (this.password1.length > 5 && this.password1.length < 25) {
        if (this.password1 != this.password2) {
          this.passwordMatch = false;
        } else {
          this.passwordMatch = true;
        }
      } else {
        this.passwordMatch = true;
      }
    },
    password2() {
      if (this.password2.length > 5 && this.password2.length < 25) {
        if (this.password1 != this.password2) {
          this.passwordMatch = false;
        } else {
          this.passwordMatch = true;
        }
      } else {
        this.passwordMatch = true;
      }
    },
  },
  computed: {
    ...mapState({
      userData: (state) => state.fetchUserBack.userData,
    }),
    ...mapGetters({
      isEditStaff: "isEditStaff",
      province_list: "fetchProvinceBack/provinceSplited",
    }),
  },
  methods: {
    telAlready(a) {
      let check = 0;
      for (let key in this.userData) {
        if (a == this.userData[key].user_tel) {
          check = 1;
        }
      }
      if (check == 0) {
        return false;
      } else {
        return true;
      }
    },
    emailAlready(a) {
      let check = 0;
      for (let key in this.userData) {
        if (a == this.userData[key].user_email) {
          check = 1;
        }
      }
      if (check == 0) {
        return false;
      } else {
        return true;
      }
    },
    async addUser() {
      if (this.$refs.form.validate()) {
        if (this.password1.length > 5 && this.password1.length < 25) {
          if (this.passwordMatch == true) {
            let userInfo = {
              user_country_tel: "+66",
              user_tel: this.tel,
              user_type: 1,
              user_email: this.email,
              user_password: this.password2,
              user_first_name: this.fname,
              user_last_name: this.lname,
              user_province: this.user_province,
              user_role: this.role,
            };
            if (this.user_province_type == 1) {
              userInfo.user_province = ["all"];
            }
            let result = await this.$store.dispatch(
              "fetchUserBack/addUser",
              userInfo
            );
            if (result === true) {
              this.$emit("setDialog", false);
              this.clear();
              this.password1 = "";
              this.password2 = "";
            } else {
              alert("เบอร์โทรศัพท์ซ้ำหรือมีบางอย่างผิดพลาด");
            }
          }
        }
      }
    },
    clear() {
      this.user_province = [];
      this.user_province_type = 1;
      this.tel = "";
      this.email = "";
      this.fname = "";
      this.lname = "";
      this.role = 1;
      this.password1 = "";
      this.password2 = "";
      this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped>
.new-user-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #141414 0%, #9e9e9e 100%);
  color: white;
  padding: 24px;
  position: relative;
}

.header-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 12px;
  margin-right: 16px;
  backdrop-filter: blur(10px);
}

.header-text {
  flex: 1;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: white;
}

.dialog-subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 4px 0 0 0;
  color: white;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.1);
}

.dialog-content {
  padding: 32px 24px;
  background: #fafafa;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.form-section:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.country-select,
.phone-field,
.email-field,
.name-field,
.role-select,
.province-select,
.password-field {
  margin-bottom: 8px;
}

.province-section {
  margin-top: 16px;
}

.province-radio {
  margin-bottom: 16px;
}

.password-alert {
  margin-top: 16px;
  border-radius: 8px;
}

.dialog-footer {
  background: white;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
}

.save-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  background: linear-gradient(45deg, #141414 0%, #9e9e9e 100%) !important;
  color: white;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(117, 117, 117, 0.4) !important;
}

/* Responsive Design */
@media (max-width: 600px) {
  .dialog-header {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
  }

  .icon-wrapper {
    margin-right: 0;
    margin-bottom: 12px;
  }

  .dialog-content {
    padding: 16px;
  }

  .form-section {
    padding: 16px;
  }
}
</style>